/**
 * Theme configuration for the application
 * This file defines all the colors and other theme variables used throughout the app
 */

// Define theme color variables that can be easily changed

export type ThemeMode = 'light' | 'dark';
export type ColorScheme = 'blue' | 'green' | 'purple' | 'rose' | 'plain';

export interface ThemeColors {
  // Base colors
  background: string;
  foreground: string;
  
  // Header
  headerBackground: string;
  headerText: string;
  headerHover: string;
  
  // Cards and containers
  cardBackground: string;
  cardBorder: string;
  
  // Form elements
  inputBackground: string;
  inputBorder: string;
  inputText: string;
  inputPlaceholder: string;
  
  // Buttons
  primaryButton: string;
  primaryButtonHover: string;
  primaryButtonText: string;
  secondaryButton: string;
  secondaryButtonHover: string;
  secondaryButtonText: string;
  
  // Links
  link: string;
  linkHover: string;
  
  // Feedback colors
  success: string;
  successText: string;
  error: string;
  errorText: string;
  warning: string;
  warningText: string;
  info: string;
  infoText: string;
  
  // Borders and dividers
  border: string;
  divider: string;
}

// Light theme colors
const baseLightTheme: ThemeColors = {
  // Base colors
  background: 'bg-neutral-50',
  foreground: 'text-neutral-900',
  
  // Header
  headerBackground: 'bg-neutral-200',
  headerText: 'text-neutral-900',
  headerHover: 'hover:bg-neutral-300',
  
  // Cards and containers
  cardBackground: 'bg-white',
  // Darker border on mobile, lighter on sm+
  cardBorder: 'border border-primary/30',
  
  // Form elements
  inputBackground: 'bg-white',
  inputBorder: 'border-neutral-300',
  inputText: 'text-neutral-900',
  inputPlaceholder: 'placeholder:text-neutral-400',
  
  // Buttons
  primaryButton: 'bg-neutral-800',
  primaryButtonHover: 'hover:bg-neutral-700',
  primaryButtonText: 'text-white',
  secondaryButton: 'bg-neutral-200',
  secondaryButtonHover: 'hover:bg-neutral-400',
  secondaryButtonText: 'text-neutral-900',
  
  // Links
  link: 'text-neutral-700',
  linkHover: 'hover:text-neutral-900',
  
  // Feedback colors
  success: 'bg-green-100',
  successText: 'text-green-700',
  error: 'bg-red-50',
  errorText: 'text-red-700',
  warning: 'bg-yellow-50',
  warningText: 'text-yellow-700',
  info: 'bg-neutral-50',
  infoText: 'text-neutral-700',
  
  // Borders and dividers
  border: 'border-primary/30',
  divider: 'border-primary/20',
};

// Dark theme colors
const baseDarkTheme: ThemeColors = {
  // Base colors
  background: 'bg-neutral-800', // Changed from bg-neutral-900
  foreground: 'text-white',
  
  // Header
  headerBackground: 'bg-neutral-900', // Set to #262626 for dark theme
  headerText: 'text-white',
  headerHover: `hover:bg-neutral-600 hover:text-white`, // Keep hover distinct
  
  // Cards and containers
  cardBackground: 'bg-neutral-700',
  // Darker border on mobile, lighter on sm+
  // Even lighter border on mobile (neutral-400), same as bg on sm+ (neutral-700)
  cardBorder: 'border border-primary/30',
  
  // Form elements
  inputBackground: 'bg-neutral-800',
  inputBorder: 'border-neutral-700',
  inputText: 'text-neutral-100',
  inputPlaceholder: 'placeholder:text-neutral-500',
  
  // Buttons
  primaryButton: 'bg-neutral-50', // Match base button dark mode (near white)
  primaryButtonHover: `hover:bg-neutral-200 hover:text-neutral-900`, // Slightly darker subtle hover
  primaryButtonText: 'text-neutral-900', // Match base button dark mode text
  secondaryButton: 'bg-neutral-800', 
  secondaryButtonHover: `hover:bg-neutral-700 hover:text-neutral-300`, 
  secondaryButtonText: 'text-white', 
  
  // Links
  link: 'text-white',
  linkHover: `hover:text-white`,
  
  // Feedback colors
  success: 'bg-green-950',
  successText: 'text-green-400',
  error: 'bg-red-950',
  errorText: 'text-red-700', // Changed to match light theme for consistency
  warning: 'bg-yellow-950',
  warningText: 'text-yellow-300',
  info: 'bg-neutral-800',
  infoText: 'text-neutral-300',
  
  // Borders and dividers
  border: 'border-primary/30',
  divider: 'border-primary/20',
};

const lightAccents: Record<ColorScheme, Partial<ThemeColors>> = {
  blue: {
    headerBackground: 'bg-blue-600',
    headerText: 'text-white',
    headerHover: 'hover:bg-blue-700 hover:text-white',
    primaryButton: 'bg-blue-600',
    primaryButtonHover: 'hover:bg-blue-700',
    link: 'text-blue-600',
    linkHover: 'hover:text-blue-800',
  },
  green: {
    headerBackground: 'bg-green-700',
    headerText: 'text-white',
    headerHover: 'hover:bg-green-800 hover:text-white',
    primaryButton: 'bg-green-600',
    primaryButtonHover: 'hover:bg-green-700',
    link: 'text-green-600',
    linkHover: 'hover:text-green-800',
  },
  purple: {
    headerBackground: 'bg-purple-600',
    headerText: 'text-white',
    headerHover: 'hover:bg-purple-700 hover:text-white',
    primaryButton: 'bg-purple-600',
    primaryButtonHover: 'hover:bg-purple-700',
    link: 'text-purple-600',
    linkHover: 'hover:text-purple-800',
  },
  rose: {
    headerBackground: 'bg-rose-600',
    headerText: 'text-white',
    headerHover: 'hover:bg-rose-700 hover:text-white',
    primaryButton: 'bg-rose-600',
    primaryButtonHover: 'hover:bg-rose-700',
    link: 'text-rose-600',
    linkHover: 'hover:text-rose-800',
  },
  plain: {},
};

const darkAccents: Record<ColorScheme, Partial<ThemeColors>> = {
  blue: {
    headerBackground: 'bg-blue-700',
    headerHover: 'hover:bg-blue-600',
    primaryButton: 'bg-blue-500',
    primaryButtonHover: 'hover:bg-blue-400',
    link: 'text-blue-400',
    linkHover: 'hover:text-blue-300',
  },
  green: {
    headerBackground: 'bg-green-700',
    // green-600 is too light for white text (3.3:1); darken on hover instead.
    headerHover: 'hover:bg-green-800',
    primaryButton: 'bg-green-500',
    primaryButtonHover: 'hover:bg-green-400',
    link: 'text-green-400',
    linkHover: 'hover:text-green-300',
  },
  purple: {
    headerBackground: 'bg-purple-700',
    headerHover: 'hover:bg-purple-600',
    primaryButton: 'bg-purple-500',
    primaryButtonHover: 'hover:bg-purple-400',
    link: 'text-purple-400',
    linkHover: 'hover:text-purple-300',
  },
  rose: {
    headerBackground: 'bg-rose-700',
    headerHover: 'hover:bg-rose-600',
    primaryButton: 'bg-rose-500',
    primaryButtonHover: 'hover:bg-rose-400',
    link: 'text-rose-400',
    linkHover: 'hover:text-rose-300',
  },
  plain: {},
};

// Get theme colors based on mode and color scheme
export function getThemeColors(mode: ThemeMode, scheme: ColorScheme): ThemeColors {
  const base = mode === 'light' ? baseLightTheme : baseDarkTheme;
  const accent = mode === 'light' ? lightAccents[scheme] : darkAccents[scheme];
  return { ...base, ...accent } as ThemeColors;
}

// Export defaults
export const defaultTheme: ThemeMode = 'light';
export const defaultColorScheme: ColorScheme = 'blue';
